import { useState, useEffect, useRef, useCallback } from "react";
import Time from "./Time";
import "./Tide.scss";

const Tide = ({ location, limit, showTitle = true }) => {
  const [tides, setTides] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshTimeout = useRef(null);

  //const tide_api = `/tidetest.json`;
  const tide_api = `https://api.inscreen.it/tidetimes.php?location=${location}`;

  const fetchTides = useCallback(() => {
    setLoading(true);
    fetch(tide_api)
      .then((res) => res.json())
      .then((data) => {
        if (data.tides) {
          const now = new Date();
          let currentIndex = -1;

          for (let i = 0; i < data.tides.length; i++) {
            const [hours, minutes] = data.tides[i].time.split(":").map(Number);
            const tideTime = new Date();
            tideTime.setHours(hours, minutes, 0, 0);

            if (tideTime <= now) {
              currentIndex = i;
            } else {
              break;
            }
          }

          // Include at least the last tide, even if all have passed
          let filteredTides = [];
          if (currentIndex >= 0 && currentIndex < data.tides.length - 1) {
            filteredTides = data.tides.slice(currentIndex);
          } else {
            filteredTides = [data.tides[data.tides.length - 1]]; // last tide only
          }

          setTides(filteredTides);

          // Schedule refresh
          let msUntilNextRefresh = null;

          if (filteredTides.length > 1) {
            // Normal case: refresh at next tide
            const [nextHours, nextMinutes] = filteredTides[1].time.split(":").map(Number);
            const nextTideTime = new Date();
            nextTideTime.setHours(nextHours, nextMinutes, 0, 0);
            msUntilNextRefresh = nextTideTime - now;
          } else {
            // No future tides left — refresh at midnight
            const midnight = new Date(now);
            midnight.setHours(24, 0, 5, 0); // small buffer after midnight
            msUntilNextRefresh = midnight - now;
          }

          if (msUntilNextRefresh > 0) {
            clearTimeout(refreshTimeout.current);
            refreshTimeout.current = setTimeout(fetchTides, msUntilNextRefresh);
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tide_api]);

  useEffect(() => {
    fetchTides();
    return () => clearTimeout(refreshTimeout.current);
  }, [fetchTides]);

  if (loading) return <div className="banner tide-banner">Loading tides...</div>;
  if (!tides.length) return <div className="banner tide-banner">No tides available</div>;

  const currentTide = tides[0];
  const nextTide = tides[1];

  // Determine if tide is rising or falling
  let tideTrend = "";
  if (nextTide) {
    tideTrend =
      currentTide.type.toLowerCase() === "low" && nextTide.type.toLowerCase() === "high"
        ? "Tide is rising"
        : currentTide.type.toLowerCase() === "high" && nextTide.type.toLowerCase() === "low"
        ? "Tide is falling"
        : "";
  } else if (currentTide) {
    // No upcoming tides, show trend based on last tide
    tideTrend =
      currentTide.type.toLowerCase() === "low"
        ? "Tide is rising"
        : currentTide.type.toLowerCase() === "high"
        ? "Tide is falling"
        : "";
  }

  return (
    <div className="banner tide-banner">
      {showTitle && (
        <div className="tide-banner-title">
          <div className="tide-banner-title-location">{location ? `${location} Tide Times` : "Tide Times"}</div>
        </div>
      )}

      <div className="tide-banner-items">
        <ul className="tide-list">
          {/* Current tide trend */}
          {currentTide && tideTrend && (
            <li className="tide-list-item current-tide">
              <div className="tide-icon">
                <img src="/img/tide/current-tide.png" alt="Current tide" />
              </div>
              <div className="tide-details">
                <div className="tide-trend">{tideTrend}</div>
              </div>
            </li>
          )}

          {/* Next tide(s) */}
          {nextTide && (
            <li className="tide-list-item next-tide">
              <div className="tide-icon">
                <img
                  src={nextTide.type.toLowerCase() === "high" ? "/img/tide/high-tide.png" : "/img/tide/low-tide.png"}
                  alt={`${nextTide.type} Tide`}
                />
              </div>
              <div className="tide-details">
                <div className="tide-trend">{nextTide.type} Tide</div>
                <div className="tide-main">{nextTide.time}</div>
                <div className="tide-height">{nextTide.height}</div>
              </div>
            </li>
          )}

          {/* Remaining tides if needed */}
          {tides.slice(2, limit ? limit : tides.length).map((tide, index) => (
            <li key={index} className="tide-list-item">
              <div className="tide-icon">
                <img
                  src={tide.type.toLowerCase() === "high" ? "/img/tide/high-tide.png" : "/img/tide/low-tide.png"}
                  alt={`${tide.type} tide`}
                />
              </div>
              <div className="tide-details">
                <div className="tide-trend">{tide.type} Tide</div>
                <div className="tide-main">{tide.time}</div>
                <div className="tide-height">{tide.height}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {showTitle && (
        <div className="tide-time">
          <Time />
        </div>
      )}
    </div>
  );
};

export default Tide;
